import { useState } from 'react';
import {
  Heart,
  Bone,
  Activity,
  Sparkles,
  Baby,
  Smile,
  Clock,
  DollarSign,
  ArrowRight,
  X,
  CheckCircle2,
  Stethoscope,
  ShieldCheck,
  Award
} from 'lucide-react';
import { treatments } from '../mockData';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { motion, AnimatePresence } from 'framer-motion';

const iconMap = {
  Heart: Heart,
  Bone: Bone,
  Activity: Activity,
  Sparkles: Sparkles,
  Baby: Baby,
  Smile: Smile
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 30, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

export const Treatments = ({ onBookConsultation }) => {
  const [selectedTreatment, setSelectedTreatment] = useState(null);

  return (
    <section id="treatments" className="py-24 bg-white/50 relative overflow-hidden">
      {/* Premium Background Decorations */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#EEFBFC] rounded-full blur-[120px] opacity-40 -translate-y-1/2 translate-x-1/4 -z-10" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-sky-50 rounded-full blur-[100px] opacity-30 -translate-x-1/4 translate-y-1/4 -z-10" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-5 py-2 bg-[#EEFBFC] text-[#1B615F] rounded-full text-sm font-bold mb-6 border border-[#1B615F]/10 shadow-sm"
          >
            <Stethoscope className="w-4 h-4" />
            <span>EXCELLENCE IN HEALTHCARE</span>
          </motion.div>
          <h2 className="text-4xl lg:text-5xl xl:text-6xl font-black text-[#1F1F1F] mb-6 tracking-tight leading-tight">
            Specialized <span className="text-[#1B615F]">Treatments</span>
          </h2>
          <p className="text-lg lg:text-xl text-gray-500 max-w-2xl mx-auto leading-relaxed">
            Access world-class medical procedures performed by internationally accredited surgeons using state-of-the-art technology.
          </p>
        </motion.div>

        {/* Treatments Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10"
        >
          {treatments.map((treatment, index) => {
            const Icon = iconMap[treatment.icon] || Activity;

            return (
              <motion.div
                key={treatment.id}
                variants={itemVariants}
                whileHover={{ y: -12 }}
                onClick={() => setSelectedTreatment(treatment)}
                className="group relative bg-white rounded-[40px] p-6 transition-all duration-500 shadow-[0_10px_40px_-15px_rgba(0,0,0,0.05)] hover:shadow-[0_30px_60px_-12px_rgba(27,97,95,0.15)] cursor-pointer flex flex-col h-full border border-gray-100/50"
              >
                {/* Image Section */}
                <div className="relative h-64 rounded-[32px] overflow-hidden mb-6">
                  <div className="absolute inset-0 bg-gray-100 animate-pulse" />
                  <img loading="lazy" decoding="async"
                    src={treatment.image}
                    alt={treatment.title}
                    className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />

                  {/* Glassmorphism Icon Badge */}
                  <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-xl p-3.5 rounded-2xl shadow-lg border border-white/30 transition-all duration-500 group-hover:scale-110 group-hover:rotate-12 group-hover:bg-[#1B615F] group-hover:text-white">
                    <Icon className="w-6 h-6 text-[#1B615F] group-hover:text-white transition-colors" />
                  </div>
                </div>

                {/* Content Section */}
                <div className="flex-grow flex flex-col">
                  <h3 className="text-2xl font-bold text-[#1F1F1F] mb-3 group-hover:text-[#1B615F] transition-colors">
                    {treatment.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-8 line-clamp-2">
                    {treatment.description}
                  </p>

                  <div className="mt-auto">
                    {/* Tags */}
                    <div className="flex flex-wrap gap-3 mb-6">
                      <div className="flex items-center gap-1.5 bg-[#EEFBFC] px-4 py-2 rounded-full text-xs font-bold text-[#1B615F] border border-[#1B615F]/10">
                        <DollarSign className="w-3.5 h-3.5" />
                        {treatment.price}
                      </div>
                      <div className="flex items-center gap-1.5 bg-gray-50 px-4 py-2 rounded-full text-xs font-bold text-gray-500 border border-gray-100">
                        <Clock className="w-3.5 h-3.5" />
                        {treatment.duration}
                      </div>
                    </div>

                    <div className="w-full h-px bg-gray-50 mb-6" />

                    {/* Footer Action */}
                    <div className="flex items-center justify-between group/action">
                      <span className="text-xs font-black text-gray-300 tracking-widest uppercase group-hover:text-[#1F1F1F] transition-colors">VIEW CLINICAL DETAILS</span>
                      <motion.div
                        whileHover={{ scale: 1.1 }}
                        className="w-10 h-10 rounded-full bg-[#EEFBFC] flex items-center justify-center text-[#1B615F] group-hover:bg-[#1B615F] group-hover:text-white transition-all duration-300"
                      >
                        <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                      </motion.div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Enhanced Treatments Dialog */}
        <Dialog open={!!selectedTreatment} onOpenChange={() => setSelectedTreatment(null)}>
          <DialogContent className="max-w-4xl bg-white rounded-[40px] p-0 overflow-hidden border-none shadow-2xl">
            <div className="flex flex-col lg:grid lg:grid-cols-2 h-full lg:h-[650px]">
              {/* Visual Side */}
              <div className="relative h-64 lg:h-full overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={selectedTreatment?.id}
                    initial={{ scale: 1.2, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.8 }}
                    src={selectedTreatment?.image}
                    alt={selectedTreatment?.title}
                    className="w-full h-full object-cover"
                  />
                </AnimatePresence>
                <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-[#1B615F]/40 to-transparent" />

                {/* Floating Elements */}
                <div className="absolute bottom-6 left-6 right-6 lg:bottom-10 lg:left-10 z-10">
                  <motion.div
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-xl rounded-full border border-white/30 text-white text-xs font-bold mb-4"
                  >
                    <Award className="w-3.5 h-3.5 text-yellow-400 fill-current" />
                    GOLD STANDARD ACCREDITATION
                  </motion.div>
                  <motion.h3
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="text-white text-3xl lg:text-4xl font-black leading-tight"
                  >
                    {selectedTreatment?.title}
                  </motion.h3>
                </div>

                <button
                  onClick={() => setSelectedTreatment(null)}
                  className="absolute top-6 right-6 bg-white/20 hover:bg-white/40 backdrop-blur-md text-white rounded-full p-2.5 transition-all hover:rotate-90 z-50 border border-white/20 shadow-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content Side */}
              <div className="p-8 lg:p-12 flex flex-col overflow-y-auto bg-white relative">
                <div className="space-y-10 flex-grow">
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="space-y-4"
                  >
                    <div className="flex items-center gap-2 text-[#1B615F]">
                      <ShieldCheck className="w-5 h-5 font-bold" />
                      <span className="text-sm font-black uppercase tracking-widest">Clinical Protocol</span>
                    </div>
                    <p className="text-gray-500 text-lg leading-relaxed">
                      {selectedTreatment?.description}
                    </p>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className="grid grid-cols-2 gap-4"
                  >
                    <div className="p-6 rounded-[32px] bg-[#EEFBFC] border border-[#1B615F]/5 group hover:border-[#1B615F]/20 transition-all">
                      <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-[#1B615F] shadow-sm mb-4">
                        <DollarSign className="w-5 h-5" />
                      </div>
                      <div className="text-xs font-black text-gray-400 uppercase tracking-widest mb-1">Packages From</div>
                      <div className="text-2xl font-black text-[#1B615F]">{selectedTreatment?.price}</div>
                    </div>
                    <div className="p-6 rounded-[32px] bg-sky-50 border border-sky-100 group hover:border-sky-200 transition-all">
                      <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-sky-600 shadow-sm mb-4">
                        <Clock className="w-5 h-5" />
                      </div>
                      <div className="text-xs font-black text-gray-400 uppercase tracking-widest mb-1">Typical Stay</div>
                      <div className="text-2xl font-black text-sky-600">{selectedTreatment?.duration}</div>
                    </div>
                  </motion.div>
                </div>

                <div className="pt-10 mt-10 border-t border-gray-100">
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      onClick={() => {
                        setSelectedTreatment(null);
                        onBookConsultation();
                      }}
                      className="w-full bg-[#1B615F] hover:bg-[#144947] text-white font-black py-8 text-xl rounded-2xl shadow-xl shadow-[#1B615F]/20 transition-all flex items-center justify-center gap-4 group"
                    >
                      BOOK FREE CONSULTATION
                      <ArrowRight className="w-6 h-6 transition-transform group-hover:translate-x-2" />
                    </Button>
                  </motion.div>
                  <div className="flex items-center justify-center gap-4 mt-6 text-gray-400">
                    <div className="flex -space-x-2">
                      {[1, 2, 3].map(i => <div key={i} className="w-6 h-6 rounded-full border-2 border-white bg-gray-200 overflow-hidden"><img loading="lazy" decoding="async" src={`https://i.pravatar.cc/100?u=${i + 20}`} alt="user" /></div>)}
                    </div>
                    <span className="text-xs font-bold leading-tight uppercase tracking-widest">
                      JOIN 2,400+ PATIENTS THIS YEAR
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};
